﻿import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Play, Info, GraduationCap } from 'lucide-react'
import CreditsModal from '../ui/CreditsModal'

import AudioControls from '../ui/AudioControls'
import { audioManager } from '../../utils/AudioManager'

import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'

interface LandingPageProps {
  onStartGame: () => void
  onShowTutorial: () => void
}

import VintageLayout from '../layout/VintageLayout';

const LandingPage: React.FC<LandingPageProps> = ({ onStartGame, onShowTutorial }) => {
  const [showCredits, setShowCredits] = useState(false)

  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuth()



  // DÃ©marrer la musique d'ambiance
  useEffect(() => {
    // Tenter de dÃ©marrer la musique
    const startMusic = async () => {
      try {
        audioManager.playMusic('landing');
      } catch (error) {
        console.log('Autoplay bloqué, attente interaction utilisateur');
        // Fallback: démarrer au premier clic
        const handleFirstClick = () => {
          console.log('Premier clic détecté : Démarrage audio forcé');
          audioManager.setMusicVolume(0.5); // S'assurer que le volume est audible
          audioManager.playMusic('landing');
          document.removeEventListener('click', handleFirstClick);
        };
        document.addEventListener('click', handleFirstClick);
      }
    };

    startMusic();

    // Nettoyage uniquement des listeners, pas de l'audio
    return () => {
      // On ne coupe plus la musique ici car GameApp va prendre le relais
      // audioManager.fadeOutMusic(1000); 
    };
  }, []);

  const [mouseX, setMouseX] = useState(0)
  const [mouseY, setMouseY] = useState(0)

  // Suivi de la souris pour l'effet de parallaxe
  const handleMouseMove = (e: React.MouseEvent) => {
    const { clientX, clientY } = e;
    const { innerWidth, innerHeight } = window;
    const x = (clientX / innerWidth - 0.5) * 2; // -1 Ã  1
    const y = (clientY / innerHeight - 0.5) * 2; // -1 Ã  1
    setMouseX(x);
    setMouseY(y);
  };

  return (
    <VintageLayout>
      <div className="fixed inset-0 z-0 overflow-hidden" onMouseMove={handleMouseMove}>
        {/* FOND FIXE (SANS PARALLAXE) */}
        <div className="absolute inset-0 w-full h-full z-0">
          {/* COUCHE 1 : FOND FLOU (NÃ‰BULOSITÃ‰) */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url("/assets/images/landing-bg-aubette.png")',
              filter: 'sepia(70%) brightness(0.15) contrast(0.7) blur(50px)',
            }}
          />

          {/* SYSTEME DE RENDU NÉBULEUX (SVG NATIVE MASK) */}

          <svg style={{ position: 'absolute', width: '100%', height: '100%' }}>
            <defs>
              {/* 1. FILTRE ORGANIQUE (Turbulence + Displacement) */}
              <filter id="nebulous-memory-filter">
                <feTurbulence
                  type="fractalNoise"
                  baseFrequency="0.012"
                  numOctaves="2"
                  seed="0"
                  result="fogTexture"
                >
                  <animate attributeName="baseFrequency" values="0.012;0.015;0.012" dur="20s" repeatCount="indefinite" />
                </feTurbulence>
                <feDisplacementMap in="SourceGraphic" in2="fogTexture" scale="100" />
              </filter>

              {/* 2. MASQUE DYNAMIQUE ANIMÉ */}
              <mask id="nebulous-mask">
                {/* Fond noir = Cache tout */}
                <rect width="100%" height="100%" fill="black" />

                {/* Cercles Blancs = Révèlent l'image (avec filtre de distorsion appliqué) */}
                <g filter="url(#nebulous-memory-filter)">
                  {/* Cercle Gauche */}
                  <circle cx="30%" cy="40%" r="0" fill="white">
                    <animate attributeName="r" from="0" to="1500" dur="15s" fill="freeze" keyTimes="0; 1" calcMode="spline" keySplines="0.42 0 0.58 1" />
                  </circle>
                  {/* Cercle Droit */}
                  <circle cx="70%" cy="60%" r="0" fill="white">
                    <animate attributeName="r" from="0" to="1500" dur="15s" begin="2s" fill="freeze" keyTimes="0; 1" calcMode="spline" keySplines="0.42 0 0.58 1" />
                  </circle>
                  {/* Cercle Central */}
                  <circle cx="50%" cy="50%" r="0" fill="white">
                    <animate attributeName="r" from="0" to="1500" dur="15s" begin="4s" fill="freeze" keyTimes="0; 1" calcMode="spline" keySplines="0.42 0 0.58 1" />
                  </circle>
                </g>
              </mask>
            </defs>
          </svg>

          {/* 3. COUCHE D'IMAGE APPLIQUANT LE MASQUE */}
          <motion.div
            className="absolute inset-0 bg-cover bg-center"
            initial={{ filter: 'blur(30px) sepia(80%) brightness(0.2)' }}
            animate={{ filter: 'blur(0px) sepia(10%) brightness(1.1)' }}
            transition={{ duration: 15, ease: "easeOut" }}
            style={{
              backgroundImage: 'url("/assets/images/landing-bg-aubette.png")',
              // Application du masque SVG défini plus haut
              mask: 'url(#nebulous-mask)',
              WebkitMask: 'url(#nebulous-mask)',
            }}
          />

          {/* RAPPEL SUBTIL GRILLE LATÉRALE (Demandé par user) */}
          <div className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-30">
            <div className="absolute top-0 bottom-0 left-0 w-1/6 bg-[linear-gradient(90deg,rgba(201,169,97,0.2)_1px,transparent_1px),linear-gradient(rgba(201,169,97,0.2)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:linear-gradient(to_right,black,transparent)]" />
            <div className="absolute top-0 bottom-0 right-0 w-1/6 bg-[linear-gradient(-90deg,rgba(201,169,97,0.2)_1px,transparent_1px),linear-gradient(rgba(201,169,97,0.2)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:linear-gradient(to_left,black,transparent)]" />
          </div>

          {/* RAPPEL SUBTIL GRILLE LATÉRALE (Demandé par user) */}
          <div className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-30">
            <div className="absolute top-0 bottom-0 left-0 w-1/6 bg-[linear-gradient(90deg,rgba(201,169,97,0.2)_1px,transparent_1px),linear-gradient(rgba(201,169,97,0.2)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:linear-gradient(to_right,black,transparent)]" />
            <div className="absolute top-0 bottom-0 right-0 w-1/6 bg-[linear-gradient(-90deg,rgba(201,169,97,0.2)_1px,transparent_1px),linear-gradient(rgba(201,169,97,0.2)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:linear-gradient(to_left,black,transparent)]" />
          </div>
        </div>

        {/* Overlay Sombre/Papier global */}
        <div className="fixed inset-0 z-5 bg-black/30 pointer-events-none" />

        {/* Papier texturÃ© global */}
        <div className="fixed inset-0 z-20 opacity-30 mix-blend-multiply pointer-events-none"
          style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/aged-paper.png")' }} />

        {/* Vignette pour recentrer */}
        <div className="fixed inset-0 z-20 bg-[radial-gradient(circle_at_center,_transparent_30%,_rgba(0,0,0,0.8)_100%)] pointer-events-none" />


        {/* UI MINIMALISTE : NAVIGATION & LOGO */}
        <nav className="absolute top-0 left-0 right-0 z-50 p-8 flex justify-end items-start pointer-events-none">

          {/* MENU TEXTUEL DORÃ‰ (TOP RIGHT) */}
          <motion.div
            className="flex gap-10 pointer-events-auto items-center"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
          >
            {isAuthenticated ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gold-100/90 hover:text-gold-400 transition-all font-serif text-sm font-bold uppercase tracking-[0.4em] drop-shadow-md px-3 py-1 border-b border-transparent hover:border-gold-500/30"
              >
                MON ESPACE
              </button>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="text-gold-100/90 hover:text-gold-400 transition-all font-serif text-sm font-bold uppercase tracking-[0.4em] drop-shadow-md px-3 py-1 border-b border-transparent hover:border-gold-500/30"
              >
                CONNEXION
              </button>
            )}
          </motion.div>
        </nav>

        {/* CENTRE DE LA PAGE : MÃ‰DAILLON ET ACTIONS (Layout Vertical) */}
        <div className="relative z-30 min-h-screen flex items-center justify-center p-4 py-20">
          <div className="flex flex-col items-center gap-16 max-w-5xl w-full">



            {/* CONTENU & ACTIONS (EN DESSOUS) */}
            <motion.div
              className="text-center flex flex-col items-center z-40 relative px-12 py-10 bg-black/50 backdrop-blur-sm rounded-3xl border border-white/10 shadow-2xl mx-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 1 }}
            >
              {/* Le scrim a été supprimé car jugé "pas propre" */}



              <p className="text-xl md:text-2xl text-gold-100 font-serif italic tracking-widest drop-shadow-[0_2px_4px_rgba(0,0,0,1)] px-8 mb-16 max-w-3xl leading-relaxed opacity-100 font-medium">
                "Plongez dans vos souvenirs... avant qu'ils ne s'effacent"
              </p>

              {/* CTA PRINCIPAL : GROS BOUTON DORÉ */}
              <motion.button
                onClick={onStartGame}
                className="group relative mb-12"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Glow externe */}
                <div className="absolute -inset-2 bg-gold-500/20 blur-xl rounded opacity-0 group-hover:opacity-100 transition duration-500" />

                <div className="relative px-20 py-6 bg-gradient-to-b from-[#f4e4c1] via-[#c9a961] to-[#8e7643] text-stone-900 font-bold text-2xl tracking-[0.3em] flex items-center gap-4 border border-[#f4e4c1]/50 shadow-[0_20px_50px_rgba(0,0,0,0.6)]">
                  <span className="uppercase">Jouer maintenant</span>
                  <Play className="w-8 h-8 fill-current" />
                </div>
              </motion.button>

              {/* SÉPARATEUR ORNEMENTAL */}
              <div className="py-8 opacity-80">
                <svg width="200" height="20" viewBox="0 0 200 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M100 20C100 20 120 10 140 10C160 10 200 10 200 10M100 20C100 20 80 10 60 10C40 10 0 10 0 10" stroke="#C9A961" strokeWidth="2" />
                  <rect x="98" y="18" width="4" height="4" transform="rotate(45 98 18)" fill="#C9A961" />
                </svg>
              </div>

              {/* BOUTONS SECONDAIRES : LIENS DORÉS ÉLÉGANTS */}
              <div className="flex justify-center gap-12">
                <button
                  onClick={() => navigate('/tutorial')}
                  className="text-gold-100 hover:text-white transition-all font-serif text-sm font-bold uppercase tracking-[0.3em] drop-shadow-[0_2px_4px_rgba(0,0,0,1)] hover:underline decoration-gold-500/50 underline-offset-8"
                >
                  TUTORIEL
                </button>

                <div className="w-1 h-1 bg-gold-500 rounded-full mt-2 opacity-50"></div>

                <button
                  onClick={() => setShowCredits(true)}
                  className="text-gold-100 hover:text-white transition-all font-serif text-sm font-bold uppercase tracking-[0.3em] drop-shadow-[0_2px_4px_rgba(0,0,0,1)] hover:underline decoration-gold-500/50 underline-offset-8"
                >
                  À PROPOS
                </button>
              </div>
            </motion.div>
          </div>
        </div>



        {/* Styles CSS pour les animations + Vintage */}
        <style>{`
        /* Fonts de la maquette */
        @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;700;900&family=Crimson+Text:ital,wght@0,400;0,600;1,400&display=swap');
        
        /* Effet grain papier vintage */
        @keyframes grain {
          0%, 100% { transform: translate(0, 0); }
          10% { transform: translate(-5%, -10%); }
          20% { transform: translate(-15%, 5%); }
          30% { transform: translate(7%, -25%); }
          40% { transform: translate(-5%, 25%); }
          50% { transform: translate(-15%, 10%); }
          60% { transform: translate(15%, 0%); }
          70% { transform: translate(0%, 15%); }
          80% { transform: translate(3%, 35%); }
          90% { transform: translate(-10%, 10%); }
        }
        
        .cta-pulse {
          animation: pulse-gold 2s infinite;
        }

        .gold-leaf-text {
          background: linear-gradient(
            to bottom, 
            #f4e4c1 0%, 
            #c9a961 22%, 
            #8e7643 50%, 
            #c9a961 78%, 
            #f4e4c1 100%
          );
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          /* Effet de biseau (Bevel) directionnel */
          filter: 
            drop-shadow(1px 1px 0px rgba(244, 228, 193, 0.4)) 
            drop-shadow(-1px -1px 0px rgba(0, 0, 0, 0.9))
            drop-shadow(0 6px 20px rgba(0,0,0,1))
            drop-shadow(0 0 25px rgba(201,169,97,0.4));
          animation: shine 8s linear infinite;
        }

        @keyframes shine {
          to { background-position: 200% center; }
        }

        .brass-plate {
          position: relative;
          background: linear-gradient(135deg, 
            #e4d5b2 0%, 
            #c9a961 45%, 
            #a38a4f 55%, 
            #8e7643 100%
          );
          color: #1a1410;
          font-family: 'Oswald', sans-serif;
          font-weight: 800;
          font-size: 0.85rem;
          letter-spacing: 0.25em;
          border: 1px solid #f4e4c1;
          box-shadow: 
            inset 0 1px 2px rgba(255,255,255,0.5),
            inset 0 -1px 2px rgba(0,0,0,0.5),
            0 10px 20px rgba(0,0,0,0.6);
          transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          overflow: hidden;
        }

        .brass-plate::before {
          content: '';
          position: absolute;
          inset: 0;
          background: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
          opacity: 0.15;
          mix-blend-mode: overlay;
          pointer-events: none;
        }

        .brass-plate:hover {
          background: linear-gradient(135deg, 
            #f4e4c1 0%, 
            #d4bc82 50%, 
            #c9a961 100%
          );
          transform: translateY(-4px) scale(1.02);
          box-shadow: 
            inset 0 1px 2px rgba(255,255,255,0.7),
            0 15px 30px rgba(0,0,0,0.7);
        }

        .brass-plate:active {
          transform: translateY(2px) scale(0.98);
          box-shadow: 
            inset 0 4px 8px rgba(0,0,0,0.6),
            0 4px 10px rgba(0,0,0,0.4);
        }

        /* Pulsation pour le Bouton Jouer */
        @keyframes pulse-gold {
          0% { box-shadow: 0 0 0 0 rgba(201, 169, 97, 0.4); }
          70% { box-shadow: 0 0 0 15px rgba(201, 169, 97, 0); }
          100% { box-shadow: 0 0 0 0 rgba(201, 169, 97, 0); }
        }

        @keyframes pulse-light {
          0%, 100% { transform: scale(1) translate(-50%, -50%); opacity: 0.2; }
          50% { transform: scale(1.4) translate(-50%, -50%); opacity: 0.4; }
        }

        .animate-pulse-light {
          animation: pulse-light 4s ease-in-out infinite;
          position: absolute;
          top: 50%;
          left: 50%;
        }

        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .animate-spin-slow {
          animation: spin-slow 60s linear infinite;
        }

        @keyframes spin-reverse {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }

        .animate-spin-reverse {
          animation: spin-reverse 45s linear infinite;
        }

        /* SystÃ¨me de particules de poussiÃ¨re */
        .dust-particle {
          position: absolute;
          background: #c9a961;
          border-radius: 50%;
          filter: blur(1px);
          pointer-events: none;
          z-index: 25;
        }

        @keyframes float-dust {
          0% { transform: translateY(0) translateX(0); opacity: 0; }
          20% { opacity: 0.4; }
          80% { opacity: 0.4; }
          100% { transform: translateY(-100vh) translateX(50px); opacity: 0; }
        }
      `}</style>

        {/* SYSTÃˆME DE PARTICULES D'ATMOSPHÃˆRE */}
        <div className="fixed inset-0 z-25 pointer-events-none overflow-hidden">
          {[...Array(25)].map((_, i) => (
            <div
              key={i}
              className="dust-particle"
              style={{
                width: Math.random() * 3 + 1 + 'px',
                height: Math.random() * 3 + 1 + 'px',
                left: Math.random() * 100 + '%',
                top: Math.random() * 100 + 100 + '%',
                opacity: Math.random() * 0.5,
                animation: `float-dust ${Math.random() * 10 + 10}s linear infinite`,
                animationDelay: Math.random() * 10 + 's'
              }}
            />
          ))}
        </div>

        {/* Modals */}
        <CreditsModal
          isOpen={showCredits}
          onClose={() => setShowCredits(false)}
        />



      </div >

      {/* VERSION (En bas à droite, Z-50) */}
      <div className="fixed bottom-4 right-6 z-50 text-gold-500/40 text-xs font-mono tracking-widest hover:text-gold-400 transition-colors cursor-help select-none pointer-events-auto">
        v3.1.0-beta.3
      </div>

      <AudioControls /> {/* Gère sa propre position fixed z-50 */}

    </VintageLayout >
  )
}

export default LandingPage
